import { keyframes } from '@emotion/react';
import { alpha, Box, ButtonBase, Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import {
  ComponentProps,
  createContext,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';
import { ArrowUpIcon } from '@/components/icons/ArrowUpIcon';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { XCircleIcon } from '@/components/icons/XCircleIcon';
import { FloatingMenuContainer } from '@/components/layout/FloatingMenuContainer';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  ANNOTATION_ID_PREFIX,
  Annotations,
} from '@/modules/pdf/PDFViewer/Annotations';
import { getScrollParent } from '@/modules/pdf/PDFViewer/Annotations.utils';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { CitationParagraph } from './FileParagraphPicker';
import {
  FileParagraphsProvider,
  useFileParagraphs,
} from './FileParagraphsProvider';
import { useParagraphFind } from './hooks/useParagraphFind';
import { ParagraphFindTextHighlights } from './ParagraphFindTextHighlights';
import { SEARCH_HIGHLIGHT_COLORS } from './searchHighlightPalettes';

const EMPTY_PARAGRAPHS: CitationParagraph[] = [];
// Share of a citation's area that a selected result must cover to replace
// the citation fill. Lower values would drop fills for partial overlaps.
const CITATION_COVER_RATIO = 0.5;
function getOffscreenDirection(
  target: Element,
  scrollParent: Element
): OffscreenReference['direction'] | undefined {
  const view = scrollParent.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  if (rect.bottom < view.top) return 'above';
  if (rect.top > view.bottom) return 'below';
  return undefined;
}

/**
 * Keeps focus in a focused answer field while the user navigates with a
 * pointer. Its blur closes the field and removes its reference highlight.
 */
const keepFieldFocus = (event: MouseEvent) => event.preventDefault();
const SEARCH_SCROLL_INSET_PX = 80;
/**
 * Marks document controls that keep a focused answer question open when they
 * receive focus, so the user can search for or go to its reference.
 */
export const KEEPS_FIELD_FOCUS_ATTRIBUTE = 'data-keeps-field-focus';
/** Events that end a scroll to the active citation: its end, or user input. */
const REVEAL_INTERRUPT_EVENTS = [
  'scrollend',
  'wheel',
  'touchstart',
  'pointerdown',
  'keydown',
] as const;

/**
 * Fill of a matching paragraph. A match that covers a citation draws no fill,
 * so the citation keeps its own fill and border.
 */
function searchFill(selected: boolean, painted: boolean) {
  if (!painted) return 'transparent';
  return selected
    ? SEARCH_HIGHLIGHT_COLORS.selectedFill
    : SEARCH_HIGHLIGHT_COLORS.matchFill;
}
// Aligns with the top viewer controls, above the inset where Find places matches.
const PEEK_TOP_OFFSET_PX = 16;
// Distance that the marker travels from the document edge as it appears.
const PEEK_TRAVEL_PX = 6;
const PEEK_ENTER = '180ms cubic-bezier(0.2, 0, 0, 1)';
const PEEK_EXIT_MS = 160;
const PEEK_EXIT = `${PEEK_EXIT_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
const peekHiddenTransform = (direction: OffscreenReference['direction']) =>
  `translateY(${direction === 'above' ? -PEEK_TRAVEL_PX : PEEK_TRAVEL_PX}px) scale(0.96)`;
const peekIn = {
  above: keyframes`from { opacity: 0; transform: ${peekHiddenTransform('above')}; }`,
  below: keyframes`from { opacity: 0; transform: ${peekHiddenTransform('below')}; }`,
};

/**
 * Keeps the marker mounted so it can fade out. It enters with an animation,
 * because a transition does not run when the marker first mounts. A hidden
 * marker is also invisible to pointers and keyboard focus.
 */
function PeekTransition({
  direction,
  shown,
  children,
}: {
  direction: OffscreenReference['direction'];
  shown: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      aria-hidden={!shown}
      // Focus on the marker keeps the focused question open.
      {...{ [KEEPS_FIELD_FOCUS_ATTRIBUTE]: true }}
      sx={{
        display: 'flex',
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : peekHiddenTransform(direction),
        visibility: shown ? 'visible' : 'hidden',
        pointerEvents: shown ? 'auto' : 'none',
        animation: shown ? `${peekIn[direction]} ${PEEK_ENTER}` : 'none',
        transition: shown
          ? 'none'
          : `opacity ${PEEK_EXIT}, transform ${PEEK_EXIT}, visibility 0s ${PEEK_EXIT_MS}ms`,
        '@media (prefers-reduced-motion: reduce)': {
          transform: 'none',
          animation: 'none',
          transition: shown ? 'none' : `visibility 0s ${PEEK_EXIT_MS}ms`,
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Marks an off-screen reference at the document edge nearest to it. It uses
 * the reference highlight style so that it reads as the reference itself.
 */
function ReferencePeek({
  direction,
  pageNumber,
  fieldLabel,
  referencePosition,
  isRemoving,
  onClick,
}: OffscreenReference & {
  fieldLabel?: string;
  referencePosition?: string;
  isRemoving?: boolean;
  onClick: () => void;
}) {
  // A solid fill in the citation's color family, so the marker stands out
  // from the document. White text passes WCAG AA on both fills. Removal uses
  // red, like the removal picker.
  const fill = isRemoving
    ? COLORS.FUNCTIONAL.ERROR[600]
    : SEARCH_HIGHLIGHT_COLORS.citationStrong;
  const hoverFill = isRemoving
    ? COLORS.FUNCTIONAL.ERROR[700]
    : COLORS.TEAL[800];
  const Icon = direction === 'above' ? ArrowUpIcon : ArrowDownIcon;
  const label = fieldLabel ?? 'Reference';
  return (
    <ButtonBase
      aria-label={`Go to ${fieldLabel ? `${fieldLabel} ` : ''}reference${referencePosition ? ` ${referencePosition}` : ''}, page ${pageNumber}`}
      onMouseDown={keepFieldFocus}
      onClick={onClick}
      sx={(theme) => ({
        height: 'fit-content',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        maxWidth: 280,
        borderRadius: '6px',
        bgcolor: fill,
        color: COLORS.PRIMITIVES.WHITE,
        boxShadow: theme.palette.shadows.md,
        transition: 'background-color 120ms ease-out',
        '&:hover': { bgcolor: hoverFill },
        '&.Mui-focusVisible': {
          outline: `2px solid ${fill}`,
          outlineOffset: '2px',
        },
      })}
    >
      <Icon size={14} />
      <Typography variant="body-xs" noWrap sx={{ color: 'inherit' }}>
        {label}
      </Typography>
      {referencePosition && (
        <Typography
          variant="body-xs"
          noWrap
          sx={{ color: 'inherit', opacity: 0.8 }}
        >
          {referencePosition}
        </Typography>
      )}
    </ButtonBase>
  );
}
/** Where the active citation is, when it is outside the visible document area. */
interface OffscreenReference {
  direction: 'above' | 'below';
  pageNumber: number;
}
/** Connects Find to the citation that the host has in focus. */
export interface FindCitation {
  /** Label of the focused question. Find names its off-screen reference. */
  fieldLabel?: string;
  /** Position of the active citation among the question's citations, e.g. "2 of 3". */
  referencePosition?: string;
  /**
   * Change to show `focusAnnotation`. If it is off screen, Find scrolls to it
   * and clears its query, unless `focusKeepsQuery` is set. If it is visible,
   * nothing changes.
   */
  focusRequest: number;
  /**
   * The current `focusRequest` keeps the Find query, so the user can return
   * to the search results. Use it when the user steps between references.
   */
  focusKeepsQuery?: boolean;
  /**
   * The active annotation that the host shows for `focusRequest`. The viewer
   * receives new annotations one render after the request, so Find waits
   * until this annotation is active.
   */
  focusAnnotation?: CitationAnnotation;
  /**
   * Number of annotations, starting at the active one, that belong to the
   * active citation. They use the strong citation colors.
   */
  primaryAnnotationCount?: number;
  /** The question is choosing a reference to remove. */
  isRemoving?: boolean;
}
type CitationAnnotation = ComponentProps<
  typeof Annotations
>['annotations'][number];
const FindContext = createContext<{
  search: ReturnType<typeof useParagraphFind>;
  /** Changes when the user clicks the off-screen reference marker. */
  markerRequest: number;
  focusRequest: number;
  focusKeepsQuery?: boolean;
  focusAnnotation?: CitationAnnotation;
  /**
   * Track the last focus request that Find completed. The record lives outside
   * the overlay, because the overlay remounts when its annotations change.
   */
  isFocusRequestHandled: (request: number) => boolean;
  markFocusRequestHandled: (request: number) => void;
  primaryAnnotationCount?: number;
  setOffscreenReference: (reference: OffscreenReference | undefined) => void;
} | null>(null);

export function ParagraphFind(
  props: ComponentProps<typeof ActiveParagraphFind> & {
    loadParagraphs?: boolean;
  }
) {
  if (props.enabled === false && !props.loadParagraphs) return props.children;

  return (
    <FileParagraphsProvider
      key={`${props.documentId}:${props.fileID}`}
      fileID={props.fileID}
      enabled={props.loadParagraphs ?? props.enabled !== false}
      processing={props.processing}
    >
      {props.enabled === false ? (
        props.children
      ) : (
        <ActiveParagraphFind {...props} />
      )}
    </FileParagraphsProvider>
  );
}

/**
 * Owns Find state independently of answer citations. Key by the displayed source.
 * A query owns the scroll position: citation changes highlight without
 * scrolling, except for a `findCitation` focus request. When the active
 * citation is off screen, a marker at the nearest edge scrolls to it.
 */
function ActiveParagraphFind({
  fileID,
  documentId,
  enabled = true,
  processing = false,
  findCitation,
  children,
}: PropsWithChildren<{
  fileID: string;
  documentId: string;
  enabled?: boolean;
  processing?: boolean;
  findCitation?: FindCitation;
}>) {
  const { data, loading, error, refetch } = useFileParagraphs();
  const search = useParagraphFind(
    processing || error
      ? EMPTY_PARAGRAPHS
      : (data?.getFileParagraphs ?? EMPTY_PARAGRAPHS)
  );
  const trackUserEvent = useTrackUserEvent();
  const tracked = useRef(false);
  const [offscreenReference, setOffscreenReferenceState] =
    useState<OffscreenReference>();
  // Scroll events report the same position many times. Keep equal values.
  const setOffscreenReference = useCallback(
    (next: OffscreenReference | undefined) =>
      setOffscreenReferenceState((current) =>
        current?.direction === next?.direction &&
        current?.pageNumber === next?.pageNumber
          ? current
          : next
      ),
    []
  );
  const [panelRevealRequest, setPanelRevealRequest] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const handledFocusRequestRef = useRef(findCitation?.focusRequest ?? 0);

  let status = '';
  if (processing) status = 'Document text is still processing';
  else if (loading) status = 'Loading extracted text…';
  else if (error) status = 'Extracted text could not load';
  else if (!data?.getFileParagraphs.length)
    status = 'Extracted text is unavailable';
  else if (search.query.trim()) {
    status = search.matches.length
      ? `${search.index + 1} of ${search.matches.length} paragraphs`
      : 'No matching paragraphs';
  }
  const canSearch =
    !processing && !loading && !error && !!data?.getFileParagraphs.length;
  // The last reference for each edge stays rendered while its marker fades out.
  const lastAbove = useRef<OffscreenReference | undefined>(undefined);
  const lastBelow = useRef<OffscreenReference | undefined>(undefined);
  if (offscreenReference?.direction === 'above')
    lastAbove.current = offscreenReference;
  if (offscreenReference?.direction === 'below')
    lastBelow.current = offscreenReference;
  const renderPeek = (reference: OffscreenReference | undefined) =>
    reference && (
      <PeekTransition
        direction={reference.direction}
        shown={offscreenReference?.direction === reference.direction}
      >
        <ReferencePeek
          {...reference}
          fieldLabel={findCitation?.fieldLabel}
          referencePosition={findCitation?.referencePosition}
          isRemoving={findCitation?.isRemoving}
          onClick={() => setPanelRevealRequest((count) => count + 1)}
        />
      </PeekTransition>
    );

  return (
    <FindContext.Provider
      value={
        enabled
          ? {
              search,
              markerRequest: panelRevealRequest,
              focusRequest: findCitation?.focusRequest ?? 0,
              focusKeepsQuery: findCitation?.focusKeepsQuery,
              focusAnnotation: findCitation?.focusAnnotation,
              isFocusRequestHandled: (request) =>
                request === handledFocusRequestRef.current,
              markFocusRequestHandled: (request) => {
                handledFocusRequestRef.current = request;
              },
              primaryAnnotationCount: findCitation?.primaryAnnotationCount,
              setOffscreenReference,
            }
          : null
      }
    >
      {enabled && lastAbove.current && (
        // Zero height keeps the document from moving when the marker appears.
        <FloatingMenuContainer
          offset={{ top: PEEK_TOP_OFFSET_PX }}
          sx={{ height: 0, display: 'flex', justifyContent: 'center' }}
        >
          {renderPeek(lastAbove.current)}
        </FloatingMenuContainer>
      )}
      {children}
      {enabled && (
        <FloatingMenuContainer offset={{ bottom: 24 }} sx={{ mx: 3, mt: 2 }}>
          {lastBelow.current && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 'calc(100% + 12px)',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {renderPeek(lastBelow.current)}
            </Box>
          )}
          {/* One row, like an editor find widget: the result count and
              navigation sit inside the input row, so the panel never changes
              height while the user searches. */}
          <Box
            role="search"
            // The focused question stays open while focus is in Find.
            {...{ [KEEPS_FIELD_FOCUS_ATTRIBUTE]: true }}
            p={1.5}
            sx={(theme) => ({
              background: COLORS.GRAY[50],
              border: `1px solid ${COLORS.GRAY[200]}`,
              borderRadius: 1,
              boxShadow: theme.palette.shadows.lg,
              // A light focus ring. The default near-black ring is too heavy
              // for a floating panel over the document.
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                { borderColor: COLORS.GRAY[300], borderWidth: '1px' },
            })}
          >
            <TextInput
              label="Search document"
              hideLabel
              inputProps={{ 'aria-label': 'Search document' }}
              inputRef={inputRef}
              placeholder="Find text in this document"
              value={search.query}
              disabled={!canSearch}
              startAdornment={<SearchSmIcon size={16} />}
              endAdornment={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography
                    variant="body-xs"
                    role="status"
                    aria-live="polite"
                    noWrap
                    sx={{ color: COLORS.TEXT.SECONDARY, mr: 0.5 }}
                  >
                    {status}
                  </Typography>
                  {error && (
                    <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => {
                        void refetch();
                      }}
                    >
                      Try again
                    </Button>
                  )}
                  <IconButton
                    icon={ChevronLeftIcon}
                    ariaLabel="Previous matching paragraph"
                    size="xs"
                    variant="transparent"
                    disabled={!canSearch || !search.matches.length}
                    onMouseDown={keepFieldFocus}
                    onClick={() => search.move(-1)}
                  />
                  <IconButton
                    icon={ChevronRightIcon}
                    ariaLabel="Next matching paragraph"
                    size="xs"
                    variant="transparent"
                    disabled={!canSearch || !search.matches.length}
                    onMouseDown={keepFieldFocus}
                    onClick={() => search.move(1)}
                  />
                  {/* Always rendered, disabled when empty, so the count
                      and arrows do not shift when the query changes. */}
                  <IconButton
                    icon={XCircleIcon}
                    ariaLabel="Clear search"
                    size="xs"
                    variant="transparent"
                    disabled={!search.query}
                    onClick={() => {
                      search.setQuery('');
                      inputRef.current?.focus();
                    }}
                  />
                </Stack>
              }
              // Select the previous query on entry, so typing replaces it.
              onFocus={(event) => event.target.select()}
              onChange={(event) => {
                search.setQuery(event.target.value);
                if (event.target.value.trim() && !tracked.current) {
                  tracked.current = true;
                  trackUserEvent('document text_search_started', {
                    documentId,
                    fileId: fileID,
                    source: 'extracted_paragraphs',
                  });
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  if (event.key === 'Escape') search.setQuery('');
                  else search.move(event.shiftKey ? -1 : 1);
                }
              }}
            />
          </Box>
        </FloatingMenuContainer>
      )}
    </FindContext.Provider>
  );
}

/** Renders only nearby search regions; navigation works before a lazy page renders. */
export function ParagraphFindOverlay({
  hideCitations,
  ...props
}: ComponentProps<typeof Annotations> & {
  /**
   * Keeps citation geometry for scrolling but paints no citation, for an
   * overlay such as the removal picker that draws the citations itself.
   */
  hideCitations?: boolean;
}) {
  const find = useContext(FindContext);
  const search = find?.search;
  const markerRequest = find?.markerRequest;
  const lastMarkerRequest = useRef(markerRequest);
  // True while the viewer scrolls to the active citation by itself.
  const revealingRef = useRef(false);
  const setOffscreenReference = find?.setOffscreenReference;
  const anchor = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState({ top: 0, bottom: 0 });
  const pagesByNumber = useMemo(
    () => new Map(props.filePages.map((page) => [page.pageNumber, page])),
    [props.filePages]
  );
  const regions = useMemo(
    () =>
      (search?.matches ?? []).flatMap((paragraph) =>
        paragraph.boundingRegions.flatMap((region, regionIndex) => {
          const pageIndex = region.PageNumber - 1;
          const viewport = props.pageViewports[pageIndex];
          const page = pagesByNumber.get(region.PageNumber);
          const offset = props.pageOffsets[pageIndex];
          if (
            !viewport ||
            offset === undefined ||
            !page?.widthInches ||
            !page.heightInches ||
            region.Polygon.length < 3
          )
            return [];
          const xs = region.Polygon.map(
            (p) => (p.x * viewport.width) / page.widthInches!
          );
          const ys = region.Polygon.map(
            (p) => (p.y * viewport.height) / page.heightInches!
          );
          const left = Math.min(...xs);
          const top = Math.min(...ys) + offset;
          const width = Math.max(...xs) - left;
          const height = Math.max(...ys) - Math.min(...ys);
          if (
            ![left, top, width, height].every(Number.isFinite) ||
            width <= 0 ||
            height <= 0
          )
            return [];
          return [
            {
              id: paragraph.id,
              regionIndex,
              pageNumber: region.PageNumber,
              left,
              top,
              width,
              height,
            },
          ];
        })
      ),
    [search?.matches, props.pageViewports, props.pageOffsets, pagesByNumber]
  );
  const nearbyRegions = useMemo(
    () =>
      regions.filter(
        (r) => r.top + r.height >= visible.top && r.top <= visible.bottom
      ),
    [regions, visible]
  );
  // The window moves on every scroll frame, but word highlights measure whole
  // text layers. Give them a list that changes only when a page enters or leaves.
  const nearbyPageKey = [
    ...new Set(nearbyRegions.map((r) => r.pageNumber)),
  ].join(',');
  const wordRegions = useMemo(() => {
    const pages = new Set(nearbyPageKey.split(',').map(Number));
    return regions.filter((r) => pages.has(r.pageNumber));
  }, [regions, nearbyPageKey]);
  const selectedRegion = regions
    .filter((r) => r.id === search?.selected?.id)
    .sort((a, b) => a.pageNumber - b.pageNumber || a.top - b.top)[0];
  const selectedTop = selectedRegion?.top;
  // Citation boxes in the same coordinates as the search regions.
  const citationBoxes = compact(
    props.annotations.map((citation) => {
      const pageIndex = (citation.PageNumber ?? 1) - 1;
      const viewport = props.pageViewports[pageIndex];
      const page = props.filePages[pageIndex];
      const offset = props.pageOffsets[pageIndex];
      if (!citation.Polygon?.length || !viewport || offset === undefined)
        return undefined;
      const scaleX = viewport.width / (page?.widthInches ?? 8.5);
      const scaleY = viewport.height / (page?.heightInches ?? 11);
      const xs = citation.Polygon.map((point) => point.x * scaleX);
      const ys = citation.Polygon.map((point) => point.y * scaleY + offset);
      return {
        left: Math.min(...xs),
        right: Math.max(...xs),
        top: Math.min(...ys),
        bottom: Math.max(...ys),
      };
    })
  );
  // One fill per paragraph: a result that covers most of a citation draws
  // no fill, so the citation keeps its own fill and border.
  const coversCitation = (region: {
    left: number;
    top: number;
    width: number;
    height: number;
  }) =>
    citationBoxes.some((box) => {
      const width =
        Math.min(box.right, region.left + region.width) -
        Math.max(box.left, region.left);
      const height =
        Math.min(box.bottom, region.top + region.height) -
        Math.max(box.top, region.top);
      const citationArea = (box.right - box.left) * (box.bottom - box.top);
      return (
        width > 0 &&
        height > 0 &&
        width * height >= citationArea * CITATION_COVER_RATIO
      );
    });

  useLayoutEffect(() => {
    if (!anchor.current) return;
    const scrollParent = getScrollParent(anchor.current);
    if (selectedTop !== undefined) {
      scrollParent.scrollTo({
        top:
          scrollParent.scrollTop +
          anchor.current.getBoundingClientRect().top -
          scrollParent.getBoundingClientRect().top +
          selectedTop -
          SEARCH_SCROLL_INSET_PX,
        behavior: 'instant',
      });
    }
  }, [selectedTop, search?.selected?.id, search?.query, search?.moveCount]);

  const setQuery = search?.setQuery;
  const hasQuery = !!search?.query.trim();
  const focusRequest = find?.focusRequest;
  const focusAnnotation = find?.focusAnnotation;
  const focusKeepsQuery = find?.focusKeepsQuery;
  const isFocusRequestHandled = find?.isFocusRequestHandled;
  const markFocusRequestHandled = find?.markFocusRequestHandled;
  /** Smoothly scrolls the active citation to the top of the viewer. */
  const revealActiveCitation = useCallback(
    (scrollParent: HTMLElement, target: Element) => {
      revealingRef.current = true;
      setOffscreenReference?.(undefined);
      scrollParent.scrollTo({
        top:
          scrollParent.scrollTop +
          target.getBoundingClientRect().top -
          scrollParent.getBoundingClientRect().top -
          SEARCH_SCROLL_INSET_PX,
        behavior: 'smooth',
      });
    },
    [setOffscreenReference]
  );
  useLayoutEffect(() => {
    if (
      focusRequest === undefined ||
      !markFocusRequestHandled ||
      isFocusRequestHandled?.(focusRequest)
    )
      return;
    if (!focusAnnotation) {
      markFocusRequestHandled(focusRequest);
      return;
    }
    // Wait until the viewer shows the requested annotation.
    if (props.annotations[props.activeAnnotationIdx] !== focusAnnotation)
      return;
    if (!anchor.current) return;
    const scrollParent = getScrollParent(anchor.current);
    const target = scrollParent.querySelector(
      `#${ANNOTATION_ID_PREFIX}${props.activeAnnotationIdx}`
    );
    if (!target) return;
    markFocusRequestHandled(focusRequest);
    // Keep the reading position when the reference is visible. Otherwise the
    // reference replaces the search results, unless the request keeps them.
    if (!getOffscreenDirection(target, scrollParent)) return;
    if (!focusKeepsQuery) setQuery?.('');
    revealActiveCitation(scrollParent, target);
  }, [
    focusRequest,
    focusAnnotation,
    focusKeepsQuery,
    isFocusRequestHandled,
    markFocusRequestHandled,
    props.annotations,
    props.activeAnnotationIdx,
    setQuery,
    revealActiveCitation,
  ]);
  useLayoutEffect(() => {
    // A marker click scrolls to the citation and keeps the query, so the user
    // can return to the search results.
    if (markerRequest === lastMarkerRequest.current) return;
    lastMarkerRequest.current = markerRequest;
    if (!anchor.current) return;
    const scrollParent = getScrollParent(anchor.current);
    const target = scrollParent.querySelector(
      `#${ANNOTATION_ID_PREFIX}${props.activeAnnotationIdx}`
    );
    if (target) revealActiveCitation(scrollParent, target);
  }, [markerRequest, props.activeAnnotationIdx, revealActiveCitation]);

  const activePageNumber =
    props.annotations[props.activeAnnotationIdx]?.PageNumber;
  // Identifies the active citation, so that layout changes do not count as a
  // new citation.
  const citationKey =
    activePageNumber === undefined
      ? undefined
      : `${props.activeAnnotationIdx}:${activePageNumber}`;
  const hasQueryRef = useRef(hasQuery);
  hasQueryRef.current = hasQuery;
  useEffect(() => {
    // Without a query, the viewer scrolls to a new citation by itself.
    revealingRef.current = citationKey !== undefined && !hasQueryRef.current;
  }, [citationKey]);
  useEffect(() => {
    if (!anchor.current || !setOffscreenReference) return;
    if (activePageNumber === undefined) {
      setOffscreenReference(undefined);
      return;
    }
    const scrollParent = getScrollParent(anchor.current);
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const target = scrollParent.querySelector(
          `#${ANNOTATION_ID_PREFIX}${props.activeAnnotationIdx}`
        );
        // A page that is not rendered yet has no target. Wait for it.
        if (!target) return;
        const direction = getOffscreenDirection(target, scrollParent);
        if (!direction) revealingRef.current = false;
        // The marker stays hidden while the viewer scrolls to the citation.
        setOffscreenReference(
          direction && !revealingRef.current
            ? { direction, pageNumber: activePageNumber }
            : undefined
        );
      });
    };
    // A scroll to the citation can stop early, for example when the user
    // scrolls during it. The marker can then show again. Safari has no
    // `scrollend`, so user scroll input also ends the reveal.
    const settle = () => {
      revealingRef.current = false;
      update();
    };
    update();
    scrollParent.addEventListener('scroll', update, { passive: true });
    for (const type of REVEAL_INTERRUPT_EVENTS) {
      scrollParent.addEventListener(type, settle, { passive: true });
    }
    return () => {
      cancelAnimationFrame(frame);
      scrollParent.removeEventListener('scroll', update);
      for (const type of REVEAL_INTERRUPT_EVENTS) {
        scrollParent.removeEventListener(type, settle);
      }
      setOffscreenReference(undefined);
    };
  }, [
    activePageNumber,
    props.activeAnnotationIdx,
    props.pageOffsets,
    props.annotations,
    setOffscreenReference,
  ]);

  useEffect(() => {
    if (!anchor.current) return;
    const scrollParent = getScrollParent(anchor.current);
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!anchor.current) return;
        const top =
          scrollParent.getBoundingClientRect().top -
          anchor.current.getBoundingClientRect().top;
        setVisible({
          top: top - scrollParent.clientHeight,
          bottom: top + scrollParent.clientHeight * 2,
        });
      });
    };
    update();
    scrollParent.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(scrollParent);
    return () => {
      cancelAnimationFrame(frame);
      scrollParent.removeEventListener('scroll', update);
      resize.disconnect();
    };
  }, []);

  return (
    <>
      <Annotations
        {...props}
        primaryAnnotationCount={find?.primaryAnnotationCount}
        scrollToActive={!search?.query.trim()}
        highlightColors={
          hideCitations
            ? {
                activeBackground: 'transparent',
                background: 'transparent',
                activeBorder: 'transparent',
                border: 'transparent',
              }
            : {
                activeBackground: SEARCH_HIGHLIGHT_COLORS.citationFill,
                background: alpha(SEARCH_HIGHLIGHT_COLORS.citationFill, 0.5),
                activeBorder: SEARCH_HIGHLIGHT_COLORS.citationBorder,
                border: alpha(SEARCH_HIGHLIGHT_COLORS.citationBorder, 0.5),
              }
        }
      />
      <Box
        ref={anchor}
        sx={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        {nearbyRegions.map(({ id, regionIndex, pageNumber, ...geometry }) => {
          const selected = id === search?.selected?.id;
          const painted = !coversCitation(geometry);
          return (
            <Box
              key={`${id}:${regionIndex}`}
              data-testid="paragraph-find-highlight"
              data-paragraph-id={id}
              data-page-number={pageNumber}
              data-selected={selected}
              sx={{
                ...geometry,
                position: 'absolute',
                zIndex: zIndexes.HIGHLIGHT,
                backgroundColor: searchFill(selected, painted),
                mixBlendMode: 'multiply',
                boxShadow:
                  selected && painted
                    ? SEARCH_HIGHLIGHT_COLORS.selectedGlow
                    : undefined,
                borderRadius: 0.5,
              }}
            />
          );
        })}
        <ParagraphFindTextHighlights
          anchor={anchor}
          query={search?.query ?? ''}
          regions={wordRegions}
          selectedParagraphId={search?.selected?.id}
        />
      </Box>
    </>
  );
}
